/**
 * Frontend gate for the Six-Box module — mirrors the server-side allowlist
 * + role check in `apps/api/src/domains/sixBox/middleware.ts`. Both must
 * change together when the pilot expands.
 */
import { useAuth } from './useAuth';
import { ROLES } from './roles';
// Leaf module with no imports of its own — safe to pull in here without
// risking a cycle back through useAuth / the axios client.
import {
  IMPERSONATABLE_TIERS,
  type ImpersonatableTier,
} from '@/api/sixBoxImpersonation';

export const SIX_BOX_FRONTEND_ALLOWLIST: ReadonlyArray<string> = [
  'CarolynGrant@jmtest.com',
  'AndrewTreanor@jmtest.com',
  'BradMorrison@jmtest.com',
  'LuwanaHarris@jmtest.com',
  'AmerMujakic@jmtest.com',
  'ZachRoss@jmtest.com',
  // Adam Hesse — B2B guest from JMTS Canada; real mail is .ca form
  'adam.hesse@jmtest.ca',
  'arulsugunakumar@jmtest.com',
  // Arul 2026-05-08 — UPN form; MSAL hands `preferred_username` (UPN)
  // back from the JWT, which is `arulsug@…` not the legal-name email.
  'arulsug@jmtest.com',
  // Carolyn 2026-05-10 — HR Calibrator pilot. Mirror in middleware.ts.
  // Arul 2026-05-12 — corrected UPNs per docs/atlas_access_rule.docx +
  // Entra lookup. Kimberley spelled with an "e".
  'kimberleysimpson@jmtest.com',
  'laniekuykendall@jmtest.com',
  // Arul 2026-09-14 — Karen Rosychuk (HR, JMTS Canada). UPN confirmed as
  // the .ca form; JMTS Canada staff are B2B guests. Mirror of
  // SIX_BOX_EMAIL_ALLOWLIST in middleware.ts.
  'Karen.Rosychuk@jmtest.ca',
  // Arul 2026-10-22 — Thomas Smith and Robert "Bob" Beltz. Mirror of
  // SIX_BOX_EMAIL_ALLOWLIST in middleware.ts, which is authoritative. Both
  // lists have to change together: the server one grants entry, this one is
  // what puts 6-Box in the nav. Tom was missing from BOTH, so the module did
  // not appear for him at all and there was nothing to click.
  'thomassmith@jmtest.com',
  'RobertBeltz@jmtest.com',
].map((e) => e.toLowerCase());

// ── Visibility tiers (Arul 2026-05-06) — keep in sync with middleware.ts ──

const CEO_EMAIL = 'andrewtreanor@jmtest.com';
const HR_LEAD_EMAILS: ReadonlyArray<string> = ['carolyngrant@jmtest.com'];
// Arul 2026-05-08 — named admin exception (CTO/platform admin support
// access). Mirror change in apps/api/.../middleware.ts ADMIN_EMAILS.
// Arul 2026-05-08 (revised) — only Andrew (CEO) and Carolyn (HR Lead)
// see all co-workers per the business rules. CTO falls back to
// `executive` tier via EXECUTIVE_EMAILS. Mirror change in
// apps/api/.../middleware.ts ADMIN_EMAILS.
const ADMIN_EMAILS: ReadonlyArray<string> = [
  'carolyngrant@jmtest.com',
];
// Andrew's direct reports (executives + HR + CTO). Carolyn lands on
// hr_admin via HR_LEAD_EMAILS first; she's listed here only so peer execs
// don't see her in their "company" view. Same for Arul — also a direct
// report of Andrew → executive tier, hidden from peers.
// Carolyn 2026-05-10 — HR Calibrator pilot. Same row set as executive
// scope=all in the UI; ratings on HR-chain rows are masked server-side.
// Mirror of HR_CALIBRATOR_EMAILS in apps/api/.../middleware.ts.
const HR_CALIBRATOR_EMAILS: ReadonlyArray<string> = [
  'kimberleysimpson@jmtest.com',
  'laniekuykendall@jmtest.com',
];

/**
 * Canada HR Admin (Arul 2026-09-11) — scoped to Adam Hesse's direct reports.
 * Mirror of CANADA_HR_ADMIN_EMAILS in apps/api/.../middleware.ts, which is
 * authoritative; this copy only exists so the UI renders the right controls.
 */
const CANADA_HR_ADMIN_EMAILS: ReadonlyArray<string> = [
  // UPN confirmed against Entra 2026-09-14. Lowercased literal — the
  // resolver lowercases the incoming address before comparing.
  'karen.rosychuk@jmtest.ca',
];

const EXECUTIVE_EMAILS: ReadonlyArray<string> = [
  'bradmorrison@jmtest.com',
  'luwanaharris@jmtest.com',
  'amermujakic@jmtest.com',
  'zachross@jmtest.com',
  'adam.hesse@jmtest.ca',
  'carolyngrant@jmtest.com',
  'arulsugunakumar@jmtest.com',
  'arulsug@jmtest.com',
];

export type SixBoxTier =
  | 'hr_admin'
  | 'executive'
  | 'hr_calibrator'
  | 'canada_hr_admin'
  | 'manager'
  | 'none';

/**
 * Canonical CEO name — Arul 2026-05-11.
 *
 * Used to hide ONLY the CEO from the Executive filter dropdown for
 * non-hr-admin viewers. The earlier revision hid every peer-exec name
 * too, which left Arul with a dropdown containing only his own name —
 * useless for filtering by other execs' downlines. Peer-exec rows are
 * still excluded from the data at the API layer (name-based
 * exclusion in `apps/api/.../service.ts`); the dropdown still surfaces
 * their names because their downline employees legitimately roll up
 * through them and an executive may want to filter by those downlines.
 */
const CEO_NAME_TOKENS: ReadonlyArray<{ firstName: string; lastName: string }> = [
  { firstName: 'andrew', lastName: 'treanor' },
];

function nameTokens(name: string | null | undefined): Set<string> {
  if (!name) return new Set();
  return new Set(
    name.toLowerCase().replace(/,/g, ' ').split(/\s+/).filter(Boolean),
  );
}

/** True when `name` matches the CEO entry by first + last name tokens.
 *  Order- / case- / middle-name- tolerant ("TREANOR, ANDREW PETER" matches). */
export function isCeoDisplayName(name: string | null | undefined): boolean {
  const tokens = nameTokens(name);
  if (tokens.size === 0) return false;
  return CEO_NAME_TOKENS.some(
    (p) => tokens.has(p.firstName) && tokens.has(p.lastName),
  );
}

export function isSixBoxEmail(email: string | undefined): boolean {
  if (!email) return false;
  return SIX_BOX_FRONTEND_ALLOWLIST.includes(email.toLowerCase());
}

export function useHasSixBoxAccess(): boolean {
  const { user } = useAuth();
  if (!user) return false;
  if (user.roles.includes(ROLES.SIX_BOX) || user.roles.includes(ROLES.ADMIN)) return true;
  return isSixBoxEmail(user.email);
}

/**
 * Explicit allowlist for the 6-Box **Admin tab** (separate from data tier).
 *
 * Arul 2026-05-14 — bug report: Admin tab was visible to every user with
 * 6-Box access. Restrict to the four people who actually administrate the
 * module: Carolyn (HR Lead), Lanie + Kimberley (HR Calibrators), and Arul
 * (Atlas platform owner — keeps the platform admin able to fix the tab if
 * it breaks). Adam Hesse / Andrew Treanor / other execs are intentionally
 * NOT here — they have data visibility but no admin actions.
 *
 * NOTE: This is *separate* from the data-visibility tier (hr_admin /
 * hr_calibrator / executive / manager). Adding Arul here does NOT grant
 * him full HR-data visibility — that's still gated by ADMIN_EMAILS /
 * HR_LEAD_EMAILS, which Carolyn explicitly scoped to herself in PR #36.
 */
export const SIX_BOX_ADMIN_TAB_ALLOWLIST: ReadonlyArray<string> = [
  'carolyngrant@jmtest.com',
  'laniekuykendall@jmtest.com',
  'kimberleysimpson@jmtest.com',
  'arulsugunakumar@jmtest.com',
  // Arul's UPN form — JWT preferred_username can carry either.
  'arulsug@jmtest.com',
].map((e) => e.toLowerCase());

export function useCanAccessSixBoxAdminTab(): boolean {
  const { user } = useAuth();
  if (!user?.email) return false;
  return SIX_BOX_ADMIN_TAB_ALLOWLIST.includes(user.email.toLowerCase());
}

export function getSixBoxTier(
  email: string | undefined,
  roles: ReadonlyArray<string>,
): SixBoxTier {
  if (!email) return 'none';
  const e = email.toLowerCase();

  // Arul 2026-09-11 — resolved first, mirroring getSixBoxAccessLevel on the
  // API. The narrow, explicitly-granted tier wins over any broad Entra role
  // that might later land on the same person by way of a group assignment.
  if (CANADA_HR_ADMIN_EMAILS.includes(e)) {
    return 'canada_hr_admin';
  }

  // Carolyn 2026-05-08 — top tier is HR-only. Atlas.Admin is the platform
  // admin role and does NOT auto-grant HR data visibility. Granted by:
  //   - explicit email lists (CEO / HR Lead / Admin emails for HR staff)
  //   - Entra HR-specific roles (Atlas.HR / Atlas.HRBP)
  // Mirrors apps/api/.../middleware.ts.
  if (
    e === CEO_EMAIL ||
    HR_LEAD_EMAILS.includes(e) ||
    ADMIN_EMAILS.includes(e) ||
    roles.includes('Atlas.HR') ||
    roles.includes('Atlas.HRBP')
  ) {
    return 'hr_admin';
  }

  // Executive tier — peers hidden from each other.
  if (EXECUTIVE_EMAILS.includes(e) || roles.includes('Atlas.Executive')) {
    return 'executive';
  }

  // HR Calibrator (Carolyn 2026-05-10). Resolves before the manager
  // fallback so allowlist membership doesn't drop calibrators back to
  // a downline view.
  if (HR_CALIBRATOR_EMAILS.includes(e)) {
    return 'hr_calibrator';
  }

  // Allowlist or SixBox role → manager (downline view).
  if (SIX_BOX_FRONTEND_ALLOWLIST.includes(e) || roles.includes(ROLES.SIX_BOX)) {
    return 'manager';
  }

  return 'none';
}

export function useSixBoxTier(): SixBoxTier {
  const { user } = useAuth();
  if (!user) return 'none';
  return getSixBoxTier(user.email, user.roles);
}

// ── Impersonation ("view as") — Arul 2026-08-21 ────────────────────────────
//
// Mirror of the server gate in apps/api/src/domains/sixBox/middleware.ts.
// Both must change together. The server is authoritative; these helpers
// exist so the UI only offers controls that will actually be honoured.

/**
 * Who may use "view as" at all: Carolyn (HR Lead) and Arul (Atlas platform
 * owner). Deliberately NOT tied to a tier — impersonation is a
 * platform-debug capability and the two people who need it sit in
 * different tiers. Mirror of SIX_BOX_IMPERSONATOR_EMAILS on the API.
 */
export const SIX_BOX_IMPERSONATOR_ALLOWLIST: ReadonlyArray<string> = [
  'carolyngrant@jmtest.com',
  'arulsugunakumar@jmtest.com',
  // Arul's UPN form — JWT preferred_username can carry either.
  'arulsug@jmtest.com',
].map((e) => e.toLowerCase());

export function canImpersonateSixBox(email: string | undefined): boolean {
  if (!email) return false;
  return SIX_BOX_IMPERSONATOR_ALLOWLIST.includes(email.toLowerCase());
}

export function useCanImpersonateSixBox(): boolean {
  const { user } = useAuth();
  return canImpersonateSixBox(user?.email);
}

/**
 * The CEO is excluded from impersonation entirely (Arul 2026-08-21).
 *
 * Matched on the email's local part rather than an exact compare — this
 * module has been bitten repeatedly by address-form drift (Adam Hesse's
 * .ca guest address, Arul's UPN vs legal-name forms), so an exact match on
 * one string is not a gate worth relying on. Directory entries are also
 * filtered by display name via `isCeoDisplayName` above.
 */
export function isCeoImpersonationTarget(email: string | undefined): boolean {
  if (!email) return false;
  const local = email.toLowerCase().split('@')[0]?.replace(/[^a-z]/g, '') ?? '';
  return local.includes('andrew') && local.includes('treanor');
}

/**
 * Tier ordering for the ceiling check. `executive` and `hr_calibrator`
 * share a rank on purpose — they are siblings, not a hierarchy. Mirror of
 * TIER_RANK on the API.
 */
// Arul 2026-09-02 — hr_calibrator now outranks executive. Executives were
// narrowed to their own downline (ADR 0004) while the calibrator tier was
// left seeing company-minus-execs, which inverted the old sibling
// relationship. Mirror of TIER_RANK on the API.
const TIER_RANK: Record<SixBoxTier, number> = {
  none: 0,
  manager: 1,
  executive: 3,
  // Arul 2026-09-11 — above `executive` deliberately. Rank answers "who may
  // assume this lens", not "how much does it see". These rows sit outside
  // every executive's downline, so only hr_admin may view as this tier.
  canada_hr_admin: 4,
  hr_calibrator: 4,
  hr_admin: 5,
};

export function sixBoxTierRank(tier: SixBoxTier): number {
  return TIER_RANK[tier] ?? 0;
}

/**
 * Tiers `realTier` may assume — its own rank and below, never above.
 *
 * This is what keeps "view as" from becoming a backdoor around the
 * visibility boundary Carolyn set in PR #36: Arul resolves to `executive`,
 * so he can inspect the executive / calibrator / director / manager lenses
 * but never hr_admin. Carolyn is hr_admin and covers the top tier
 * natively, so the full matrix is still verifiable between the two of them.
 */
export function impersonatableTiersFor(
  realTier: SixBoxTier,
): ImpersonatableTier[] {
  const ceiling = sixBoxTierRank(realTier);
  return IMPERSONATABLE_TIERS.filter((t) => sixBoxTierRank(t) <= ceiling);
}

/** Tiers the signed-in user may assume. Empty when they may not impersonate. */
export function useImpersonatableTiers(): ImpersonatableTier[] {
  const { user } = useAuth();
  if (!canImpersonateSixBox(user?.email)) return [];
  return impersonatableTiersFor(getSixBoxTier(user?.email, user?.roles ?? []));
}

/**
 * The tier the page should render as, given the active lens.
 *
 * Impersonation is only worth having if the UI tells the truth about what
 * the lens can reach — a tab, column or HR-notes field that stays visible
 * while the API refuses the underlying data makes the audit worthless and
 * reads as a bug. A person lens resolves by email alone, mirroring
 * `getSixBoxAccessLevelByEmail` on the API; we don't hold other people's
 * Entra role claims in the browser.
 *
 * Note this deliberately does NOT cover the Admin tab, which is gated by
 * an email allowlist that is explicitly separate from the data tier (see
 * SIX_BOX_ADMIN_TAB_ALLOWLIST above). Hiding it mid-audit would also strip
 * away the control surface you are auditing from.
 */
export function effectiveSixBoxTier(
  realTier: SixBoxTier,
  lens: { email: string | null; role: ImpersonatableTier | null },
): SixBoxTier {
  if (lens.role) return lens.role;
  if (lens.email) return getSixBoxTier(lens.email, []);
  return realTier;
}

/**
 * True when `targetEmail` is a lens the caller may actually open: not the
 * CEO, has 6-Box access at all, and sits at or below the caller's own tier.
 *
 * Used to filter the directory picker so it never offers a row the server
 * would answer with 403 / 404. Tier is resolved from the email alone,
 * matching `getSixBoxAccessLevelByEmail` on the API — we don't hold other
 * people's Entra role claims in the browser.
 */
export function isImpersonationTargetAllowed(
  realTier: SixBoxTier,
  targetEmail: string | undefined,
): boolean {
  if (!targetEmail) return false;
  if (isCeoImpersonationTarget(targetEmail)) return false;
  const targetTier = getSixBoxTier(targetEmail, []);
  if (targetTier === 'none') return false;
  return sixBoxTierRank(targetTier) <= sixBoxTierRank(realTier);
}
